import { ReactNode } from "react";
import { FeedbackType } from "@allaria-tech/matter/Feedback";
import { RootState } from "../../../app/store";
import { WhatsappLink } from "../../../utils";
import { LivenessState } from "../interfaces/types";

export const livenessStep = {
  name: "Validá tu identidad",
  step: 1,
};

export const LIVENESS_FEEDBACK_STATES: LivenessState[] = [
  "CAMERA_ACCESS_REQUIRED",
  "VALIDATED",
  "NOT_VALIDATED",
];

export const isLivenessFeedbackVisible = ({
  livenessState,
  isValid,
}: {
  livenessState: LivenessState;
  isValid: boolean;
}) =>
  LIVENESS_FEEDBACK_STATES.includes(livenessState) ||
  (!livenessState && isValid);

export const hideLivenessStepHeader = (state: RootState) =>
  isLivenessFeedbackVisible(state.liveness);

const NOT_RECOGNIZED_TITLE = "Lo sentimos, no pudimos reconocerte.";

export const IDENTITY_MISMATCH_ERROR = "FACE_LIVENESS_IDENTITY_MISMATCH";

const BLOCKING_LIVENESS_ERRORS = [
  "MAX_ATTEMPTS_ERROR",
  IDENTITY_MISMATCH_ERROR,
];

export const getErrorData = ({
  error,
  handleRequirementsClick,
}: {
  error: string | null;
  handleRequirementsClick: () => void;
}) => {
  let errorType: FeedbackType = "ERROR";
  let errorTitle: string | undefined = NOT_RECOGNIZED_TITLE;
  let errorDescription: string | undefined;
  let errorContent: ReactNode;

  if (BLOCKING_LIVENESS_ERRORS.includes(error ?? "")) {
    errorTitle = "No pudimos validar tu identidad";
    errorContent = (
      <span>
        No fue posible completar la validación de identidad.{" "}
        <WhatsappLink>Contactá a soporte</WhatsappLink> para continuar.
      </span>
    );
    return { errorType, errorTitle, errorDescription, errorContent };
  }

  switch (error) {
    case "CAMERA_FRAMERATE_ERROR":
      errorType = "WARNING";
      errorTitle = undefined;
      errorContent = (
        <span>
          Tu cámara o buscador de internet no cumple los{" "}
          <button type="button" onClick={handleRequirementsClick}>
            requisitos mínimos
          </button>
        </span>
      );
      break;
    case "CONNECTION_TIMEOUT":
      errorType = "WARNING";
      errorTitle = "Buscá un lugar con mejor conexión.";
      break;
    case "TIMEOUT":
      errorDescription = "Rellená el óvalo con tu cara.";
      break;
    case "FACE_DISTANCE_ERROR":
      errorDescription = "Asegurate de no acercarte durante la captura.";
      break;
    default:
      errorDescription = "Por favor, intentalo nuevamente.";
  }

  return { errorType, errorTitle, errorDescription, errorContent };
};

export const livenessTexts = {
  waitingCameraPermissionText: "Esperando el acceso a tu cámara",
  startScreenBeginCheckText: "Comenzar captura",
  hintConnectingText: "Conectando...",
  hintVerifyingText: "Verificando...",
  hintCheckCompleteText: "Completado",
  goodFitCaptionText: "Mantené tu posición",
  goodFitAltText: "Encaja perfectamente en el óvalo",
  hintTooCloseText: "Alejate",
  hintTooFarText: "Acercate",
  hintCenterFaceText: "Centrá tu cara",
  hintFaceOffCenterText: "Centrá tu cara",
  hintMoveFaceFrontOfCameraText: "Movete enfrente de la cámara",
  hintTooManyFacesText: "Asegurate que haya solo una cara",
  hintFaceDetectedText: "Cara detectada",
  hintCanNotIdentifyText: "Movete enfrente de la cámara",
  hintIlluminationTooDarkText: "Busca un lugar con mejor iluminación",
  hintIlluminationTooBright: "Busca un lugar con menos luz",
  hintHoldFaceForFreshnessText: "Mantené tu posición",
  hintMatchIndicatorText: "50% completado. Acercate",
  tryAgainText: "Reintentar",
  retryCameraPermissionsText: "Reintentar",
  cameraNotFoundHeadingText: "Permití el acceso a tu cámara",
};
